import React, { useMemo, useState } from 'react'
import { ExchangeRateSourceType } from '@/app/constants/configuration/exchangeRateSourceType'
import { ExchangeRateConfigurationModel } from '@/app/models/configuration/exchangeRateConfigurationModel'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { Pagination } from '@/modules/shared/components/Pagination'
import { SearchPopover } from './SearchPopover'
import { Table } from './Table'
import { EmptyScreen } from './EmptyScreen'

type Props = {
  exchangeRateDate: string
  sourceType: ExchangeRateSourceType
  rates: ExchangeRateConfigurationModel[]
  handleDownloadExchangeRates: () => void
}

const DEFAULT_PER_PAGE = 10
const DEFAULT_SORT_KEY = 'currencyCode'

export const ExchangeRateContainer = ({
  exchangeRateDate,
  sourceType,
  rates,
  handleDownloadExchangeRates,
}: Props) => {
  const [phrase, setPhrase] = useState<string>('')
  const [page, setPage] = useState<number>(1)
  const [perPage, setPerPage] = useState<number>(DEFAULT_PER_PAGE)
  const [sortKey, setSortKey] = useState<string>(DEFAULT_SORT_KEY)
  const [sortAsc, setSortAsc] = useState<boolean>(true)
  const { tSafe } = useLocalize()

  const isFutureDate = useMemo(() => {
    const dateStr = (exchangeRateDate || '').slice(0, 10)
    if (!dateStr) return false
    const todayStr = new Date().toISOString().slice(0, 10)
    return dateStr > todayStr
  }, [exchangeRateDate])

  const guardedDownload = () => {
    if (isFutureDate) return
    handleDownloadExchangeRates()
  }

  const handleHeaderClick = (key: string) => {
    if (sortKey === key) {
      setSortAsc((s) => !s)
    } else {
      setSortKey(key)
      setSortAsc(true)
    }
  }

  const filtered = useMemo(() => {
    if (phrase) {
      const needle = phrase.toLowerCase()
      return rates.filter((r) =>
        [r.currency.code, r.currency.id, r.currency.name, r.exchangeRate]
          .filter(Boolean)
          .some((v) => v.toString().toLowerCase().includes(needle)),
      )
    }
    return rates
  }, [rates, phrase])

  const sorted = useMemo(() => {
    const data = [...filtered]

    data.sort((a, b) => {
      let aValue: string | number
      let bValue: string | number

      switch (sortKey) {
        case 'exchangeRateDate':
          aValue = new Date(a.exchangeRateDate).getTime()
          bValue = new Date(b.exchangeRateDate).getTime()
          break
        case 'currencyName':
          aValue = a.currency.name || ''
          bValue = b.currency.name || ''
          break
        case 'currencyCode':
          aValue = a.currency.code || ''
          bValue = b.currency.code || ''
          break
        case 'exchangeRate':
          aValue = a.exchangeRate
          bValue = b.exchangeRate
          break
        default:
          return 0
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortAsc ? aValue - bValue : bValue - aValue
      }

      const aStr = String(aValue).toLowerCase()
      const bStr = String(bValue).toLowerCase()

      return sortAsc ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr)
    })

    return data
  }, [filtered, sortKey, sortAsc])

  const pagged = useMemo(() => {
    const startIndex = (page - 1) * perPage
    const endIndex = startIndex + perPage
    return sorted.slice(startIndex, endIndex)
  }, [sorted, perPage, page])

  if (!rates.length) {
    return (
      <div data-test-id="exchange-rate-container">
        <EmptyScreen
          exchangeRateDate={exchangeRateDate}
          sourceType={sourceType}
          handleDownloadExchangeRates={guardedDownload}
          isFutureDate={isFutureDate}
        />
      </div>
    )
  }

  const hasResults = filtered.length > 0

  return (
    <div
      data-test-id="exchange-rate-container"
      className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-2 tw-pt-3"
    >
      <div className="tw-flex tw-items-center">
        <SearchPopover
          search={phrase}
          setSearch={setPhrase}
          searchResults={filtered.length || 0}
        />
      </div>
      <Table
        sourceType={sourceType}
        rates={pagged}
        sortKey={sortKey}
        sortAsc={sortAsc}
        onHeaderClick={handleHeaderClick}
      />
      {hasResults ? (
        <Pagination
          currentPage={page}
          totalItems={filtered.length || 0}
          pageSize={perPage}
          onPageChange={setPage}
          onPageSizeChange={setPerPage}
        />
      ) : (
        <div className="tw-py-8 tw-text-center tw-text-sm tw-text-content-base/70">
          {tSafe('common.no-search-results', 'Brak wyników wyszukiwania')}
        </div>
      )}
    </div>
  )
}
